import { CurrencyAmount } from '../CurrencyAmount';

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string; dataKey: string; payload?: Record<string, unknown> }>;
  label?: string;
}

const getTzLabel = (payload: ChartTooltipProps['payload']): string =>
  (payload?.[0]?.payload?.timezoneLabel as string) || 'UTC';

// Module-level tooltip components (stable references - not recreated on each render)
export const RequestTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (active && payload && payload.length) {
    const dateStr = (payload[0]?.payload?.formattedDate as string) || (payload[0]?.payload?.date as string);
    const tzLabel = getTzLabel(payload);
    const displayLabel = dateStr ? `${dateStr} (${tzLabel})` : label;
    return (
      <div className="bg-gray-900 dark:bg-gray-950 border border-gray-600 dark:border-gray-500 rounded-md p-2.5 shadow-2xl">
        <p className="text-xs font-bold text-white mb-2 border-b border-gray-700 pb-1.5">
          {displayLabel}
        </p>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={entry.dataKey || entry.name || index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm flex-shrink-0 border border-gray-700" style={{ backgroundColor: entry.color }} />
                <span className="text-sm text-gray-200">{entry.name}</span>
              </div>
              <span className="text-sm font-bold text-white tabular-nums">{entry.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

export const CostTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (active && payload && payload.length) {
    const dateStr = (payload[0]?.payload?.formattedDate as string) || (payload[0]?.payload?.date as string);
    const tzLabel = getTzLabel(payload);
    const displayLabel = dateStr ? `${dateStr} (${tzLabel})` : label;
    return (
      <div className="bg-gray-900 dark:bg-gray-950 border border-gray-600 dark:border-gray-500 rounded-md p-2.5 shadow-2xl">
        <p className="text-xs font-bold text-white mb-2 border-b border-gray-700 pb-1.5">
          {displayLabel}
        </p>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={entry.dataKey || entry.name || index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm flex-shrink-0 border border-gray-700" style={{ backgroundColor: entry.color }} />
                <span className="text-sm text-gray-200">{entry.name}</span>
              </div>
              <CurrencyAmount amount={entry.value} className="text-sm font-bold text-white tabular-nums" showToggleHint={false} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

export const ModelUsageTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (active && payload && payload.length) {
    const sortedPayload = [...payload].filter(entry => entry.value > 0).sort((a, b) => b.value - a.value);
    const tzLabel = getTzLabel(payload);
    const formattedDate = (payload[0]?.payload?.formattedDate as string) || label;
    const displayLabel = formattedDate ? `${formattedDate} (${tzLabel})` : label;
    return (
      <div className="bg-gray-900 dark:bg-gray-950 border border-gray-600 dark:border-gray-500 rounded-md p-2.5 shadow-2xl">
        <p className="text-xs font-bold text-white mb-2 border-b border-gray-700 pb-1.5">
          {displayLabel}
        </p>
        <div className="space-y-2">
          {sortedPayload.map((entry, index) => (
            <div key={entry.dataKey || entry.name || index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm flex-shrink-0 border border-gray-700" style={{ backgroundColor: entry.color }} />
                <span className="text-sm text-gray-200 truncate max-w-[200px]">{entry.name}</span>
              </div>
              <span className="text-sm font-bold text-white tabular-nums">{entry.value.toFixed(1)}%</span>
            </div>
          ))}
          {sortedPayload.length === 0 && <p className="text-sm text-gray-400 italic">No usage data</p>}
        </div>
      </div>
    );
  }
  return null;
};

export const ApiKeyTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (active && payload && payload.length) {
    const dateStr = (payload[0]?.payload?.formattedDate as string) || (payload[0]?.payload?.date as string);
    const tzLabel = getTzLabel(payload);
    const displayLabel = dateStr ? `${dateStr} (${tzLabel})` : label;
    return (
      <div className="bg-gray-900 dark:bg-gray-950 border border-gray-600 dark:border-gray-500 rounded-md p-2.5 shadow-2xl">
        <p className="text-xs font-bold text-white mb-2 border-b border-gray-700 pb-1.5">
          {displayLabel}
        </p>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={entry.dataKey || entry.name || index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm flex-shrink-0 border border-gray-700" style={{ backgroundColor: entry.color }} />
                <span className="text-sm text-gray-200">{entry.name}</span>
              </div>
              <span className="text-sm font-bold text-white tabular-nums">{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};
